/**
 * Total Savings — PDP Theme Block
 *
 * Renders the total amount saved on the current order, driven by the selected
 * Quantity Break offer. Follows the same data flow as price-sync.js:
 *
 *   1. Preferred source: the shared price-override channel
 *      (window.Elixir_PriceOverride) — the Quantity Break block publishes the
 *      selected tier's final { price, compareAtPrice } totals there, with
 *      custom pricing and subscription discounts already applied.
 *   2. Fallback: variant compare-at price × form quantity via
 *      Elixir_GetDisplayPrice (covers PDPs without a Quantity Break block).
 *
 * The banner hides itself when savings are zero or negative.
 */
(function () {
  "use strict";

  var rafPending = false;

  function getBanners() {
    return document.querySelectorAll("[data-elixir-total-savings]");
  }

  function getVariants() {
    return window.__elixirProductVariants || [];
  }

  function getVariantFromForm() {
    var id = typeof Elixir_GetProductFormVariantId === "function" ? Elixir_GetProductFormVariantId() : null;
    var variants = getVariants();
    if (!id || !variants.length) return variants[0] || null;
    return (
      variants.find(function (v) {
        return String(v.id) === String(id);
      }) ||
      variants[0] ||
      null
    );
  }

  function getQuantity() {
    var form = typeof Elixir_GetProductForm === "function" ? Elixir_GetProductForm() : null;
    if (!form) return 1;
    var input = form.querySelector('input[name="quantity"]');
    if (!input && form.id) {
      input = document.querySelector('input[name="quantity"][form="' + form.id + '"]');
    }
    var qty = input ? parseInt(input.value, 10) : 1;
    return isNaN(qty) || qty < 1 ? 1 : qty;
  }

  /** Resolve current { price, compareAtPrice } totals in cents, or null. */
  function getTotals() {
    var override = window.Elixir_PriceOverride ? window.Elixir_PriceOverride.get() : null;
    if (override) {
      return { price: override.price, compareAtPrice: override.compareAtPrice };
    }

    if (typeof window.Elixir_GetDisplayPrice !== "function") return null;
    var v = getVariantFromForm();
    if (!v) return null;

    var compareAt = v.compare_at_price != null ? v.compare_at_price : v.compareAtPrice != null ? v.compareAtPrice : 0;

    return window.Elixir_GetDisplayPrice({
      price: v.price,
      quantity: getQuantity(),
      multiplyByQuantity: true,
      compareAtPrice: compareAt,
      applySubscriptionDiscount: true,
      variantId: v.id,
    });
  }

  function render() {
    var banners = getBanners();
    if (!banners.length) return;

    if (typeof window.Elixir_FormatMoney !== "function") return;

    var totals = getTotals();
    if (!totals) return;

    var savings = Math.max(0, Math.round(totals.compareAtPrice) - Math.round(totals.price));
    var pct = totals.compareAtPrice > 0 ? Math.round((savings * 100) / totals.compareAtPrice) : 0;

    banners.forEach(function (banner) {
      var placeholder = null;
      var wrap = banner.closest("[data-elixir-total-savings-wrap]");
      if (wrap) placeholder = wrap.querySelector("[data-ts-placeholder]");

      if (savings <= 0) {
        banner.hidden = true;
        if (placeholder) placeholder.hidden = false;
        return;
      }

      banner.hidden = false;
      if (placeholder) placeholder.hidden = true;

      var textEl = banner.querySelector("[data-ts-text]");
      if (!textEl) return;

      var tpl = banner.dataset.tsTemplate || "You're saving {amount} on this order";
      var html = tpl
        .split("{amount}")
        .join('<span class="total-savings-amount">' + window.Elixir_FormatMoney(savings) + "</span>")
        .split("{percent}")
        .join('<span class="total-savings-amount">' + pct + "%</span>");
      textEl.innerHTML = html;

      // One-shot pop animation, only when savings actually increase after the
      // first paint. Decreases stay silent on purpose: celebrating a shrinking
      // number would just highlight a loss at the point of purchase.
      var prev = banner.dataset.tsLastSavings;
      banner.dataset.tsLastSavings = String(savings);
      if (banner.hasAttribute("data-ts-animate") && prev !== undefined && savings > parseInt(prev, 10)) {
        // innerHTML above recreated the spans, so adding the class here
        // starts the animation fresh — no need to remove/reflow.
        textEl.querySelectorAll(".total-savings-amount").forEach(function (el) {
          el.classList.add("ts-changed");
        });
      }
    });
  }

  function scheduleRender() {
    if (rafPending) return;
    rafPending = true;
    requestAnimationFrame(function () {
      rafPending = false;
      render();
    });
  }

  // Same state-change events price-sync.js reacts to.
  document.addEventListener("quantity_break:changed", scheduleRender);
  document.addEventListener("quantity:changed", scheduleRender);
  document.addEventListener("elixir:price-override-changed", scheduleRender);
  document.addEventListener("subscriptionContext:updated", scheduleRender);
  document.addEventListener("simple-variant-picker:variantChange", scheduleRender);
  document.addEventListener("elixir-pricing-ready", scheduleRender);

  // Theme editor: re-render after section/block re-renders.
  if (window.Shopify && Shopify.designMode) {
    document.addEventListener("shopify:section:load", scheduleRender);
    document.addEventListener("shopify:block:select", scheduleRender);
  }

  // Initial pass — deferred scripts run in document order, so any QB block
  // rendered earlier has already committed its preselected tier by now.
  scheduleRender();
})();
